// ---------------------------------------------------------------------------
// DiCharts SDK – Multi-Layout Support
// ---------------------------------------------------------------------------

import { DiChart } from '../../dicharts-lib/dist/index.js';
import type { DiChartInstance } from '../../dicharts-lib/dist/index.js';
import type { CandleStyle } from './types';
import { TIMEFRAMES } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LayoutId = 'single' | '2v' | '2h' | '1-2r' | '4grid';

export const LAYOUT_PANE_COUNT: Record<LayoutId, number> = {
  single: 1, '2v': 2, '2h': 2, '1-2r': 3, '4grid': 4,
};

const SECONDARY_TFS = ['15m', '1h', '4h'];

export interface SecondaryPane {
  container: HTMLDivElement;
  chart: DiChartInstance;
  tf: string;
}

// ---------------------------------------------------------------------------
// Data generation for secondary panes
// ---------------------------------------------------------------------------

export function generateSecondaryData(
  sourceData: readonly (readonly [number, number, number, number, number])[],
  intervalMs: number,
): { candles: (readonly [number, number, number, number, number])[]; volumes: number[] } {
  const candles: (readonly [number, number, number, number, number])[] = [];
  const volumes: number[] = [];
  const count = Math.min(sourceData.length, 200);
  const lastCandle = sourceData[sourceData.length - 1];
  let price = lastCandle ? lastCandle[4] : 42_500;
  const now = Date.now();

  for (let i = 0; i < count; i++) {
    const ts = now - (count - i) * intervalMs;
    const open = price;
    const change = (Math.random() - 0.48) * price * 0.008;
    const close = open + change;
    const high = Math.max(open, close) + Math.random() * price * 0.003;
    const low = Math.min(open, close) - Math.random() * price * 0.003;
    candles.push([ts, open, high, low, close] as const);
    volumes.push(Math.round(50 + Math.random() * 4950));
    price = close;
  }
  return { candles, volumes };
}

// ---------------------------------------------------------------------------
// Layout manager
// ---------------------------------------------------------------------------

function el(tag: 'div', cls?: string): HTMLDivElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export async function setLayout(
  layout: LayoutId,
  chartArea: HTMLElement,
  primaryBadge: HTMLElement,
  currentTheme: 'dark' | 'light',
  currentStyle: CandleStyle,
  currentTimeframeKey: string,
  symbolName: string,
  sourceData: readonly (readonly [number, number, number, number, number])[],
  secondaryPanes: SecondaryPane[],
): Promise<SecondaryPane[]> {
  // Dispose and remove old secondary panes
  for (const p of secondaryPanes) {
    p.chart.dispose();
    p.container.remove();
  }

  chartArea.dataset.layout = layout;

  // Update primary badge
  if (layout === 'single') {
    primaryBadge.style.display = 'none';
  } else {
    const tfInfo = TIMEFRAMES.find(t => t.key === currentTimeframeKey);
    primaryBadge.textContent = `${symbolName || 'BTC/USD'} \u00b7 ${tfInfo?.label ?? currentTimeframeKey}`;
    primaryBadge.style.display = '';
  }

  // Create secondary panes
  const newPanes: SecondaryPane[] = [];
  const count = LAYOUT_PANE_COUNT[layout] - 1;

  for (let i = 0; i < count; i++) {
    const tfKey = SECONDARY_TFS[i] || '1h';
    const tfInfo = TIMEFRAMES.find(t => t.key === tfKey);
    const div = el('div', 'dw-pane');
    chartArea.appendChild(div);

    // Timeframe badge
    const badge = el('div', 'dw-pane-badge');
    badge.textContent = `${symbolName || 'BTC/USD'} \u00b7 ${tfInfo?.label ?? tfKey}`;
    div.appendChild(badge);

    // Generate data for this timeframe
    const data = generateSecondaryData(sourceData, tfInfo?.intervalMs ?? 60_000);
    const paneChart = await DiChart.create(div, {
      theme: currentTheme,
      candles: { data: data.candles, style: currentStyle },
      volume: { enabled: true, data: data.volumes },
    });

    newPanes.push({ container: div, chart: paneChart, tf: tfKey });
  }

  return newPanes;
}

export function updatePrimaryBadge(
  primaryBadge: HTMLElement,
  layout: LayoutId,
  currentTimeframeKey: string,
  symbolName: string,
): void {
  if (layout === 'single') {
    primaryBadge.style.display = 'none';
    return;
  }
  const tfInfo = TIMEFRAMES.find(t => t.key === currentTimeframeKey);
  primaryBadge.textContent = `${symbolName || 'BTC/USD'} \u00b7 ${tfInfo?.label ?? currentTimeframeKey}`;
  primaryBadge.style.display = '';
}

// ---------------------------------------------------------------------------
// Order lines
// ---------------------------------------------------------------------------

export function syncOrderLines(
  chartInstance: DiChartInstance,
  lastPrice: number,
  showBuyLine: boolean,
  showTPLine: boolean,
  showSLLine: boolean,
): void {
  chartInstance.clearOrderLines();
  const price = lastPrice || 42_500;
  if (showBuyLine) chartInstance.addOrderLine({ id: 'buy', price: price * 0.995, label: 'Buy Limit', color: '#26a69a', style: 'dashed' });
  if (showTPLine) chartInstance.addOrderLine({ id: 'tp', price: price * 1.02, label: 'Take Profit', color: '#FFD700', style: 'dashed' });
  if (showSLLine) chartInstance.addOrderLine({ id: 'sl', price: price * 0.98, label: 'Stop Loss', color: '#ef5350', style: 'dashed' });
}
